import { Link } from "react-router-dom";
import PublicLayout from "@/components/public/PublicLayout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import {
  Sparkles, ReceiptText, FileText, Package, CreditCard, BarChart3,
  Users, Wallet, Calculator, Check, ArrowRight, Building2, Paintbrush,
  Hammer, Sofa, Zap, Wrench, Store, Briefcase, ShieldCheck, MapPin,
  Star, Cloud, Smartphone, Wifi, WifiOff, Lock, Percent, Barcode,
  MessageCircle, Layers, Printer, FileCheck, ArrowUpRight, Tag,
  ScanLine, UserCog, Globe, Award, TrendingUp, Clock,
  ShoppingBag, ShoppingCart, Gem, Shirt, Utensils, Cross, Building,
  BedDouble, Scissors, Bus, Phone
} from "lucide-react";
import { motion } from "framer-motion";

/* ---------------------------------- data ---------------------------------- */
const TRUST_STATS = [
  { icon: Users, k: "1M+", label: "MSMEs Empowered" },
  { icon: MapPin, k: "28 States", label: "Active Across India" },
  { icon: Globe, k: "9+", label: "Indian Languages" },
  { icon: ReceiptText, k: "5L+", label: "Bills Every Day" },
];

const JOURNEY = [
  { n: "01", time: "08:00 AM · ON THE MOVE", title: "Start Anywhere", icon: Smartphone,
    desc: "Check stock on the TheBillPro app while travelling. Reach your shop and your desktop stays perfectly in sync.",
    tag: "Multi-Device Control" },
  { n: "02", time: "11:00 AM · BUSY HOURS", title: "Role-Based Access", icon: UserCog,
    desc: "Your sales staff creates bills from their own logins. You stay relaxed with owner-only permissions.",
    tag: "Multi-User Control" },
  { n: "03", time: "04:00 PM · NET DOWN", title: "Works Without Internet", icon: WifiOff,
    desc: "Internet gone? No problem. Offline billing keeps the counter moving; everything syncs when you reconnect.",
    tag: "Always-On Billing" },
  { n: "04", time: "09:00 PM · CLOSING TIME", title: "Data Synced & Secured", icon: Cloud,
    desc: "As you close for the day, your data is already backed up to the cloud. Your hard work is safe.",
    tag: "Cloud Auto-Backup" },
];

const FEATURES = [
  { icon: CreditCard, title: "Multiple Payment Modes", desc: "Cash, UPI, card, cheque or bank transfer — every mode recorded on the bill automatically." },
  { icon: Percent, title: "Discount & Tax Handling", desc: "Item-level or bill-level discounts with automatic CGST/SGST/IGST calculation." },
  { icon: FileCheck, title: "E-Invoice & E-Way Bill Ready", desc: "Generate GST-compliant invoices and e-way bills without leaving the platform." },
  { icon: FileText, title: "Automated Bill Numbering", desc: "Auto-organised numbering by financial year — audit and GSTR filing become effortless." },
  { icon: MessageCircle, title: "WhatsApp Sharing & Reminders", desc: "Send invoices and payment reminders on WhatsApp in one tap. Get paid faster." },
  { icon: Barcode, title: "Barcode Generator & Scanner", desc: "Print barcodes and scan them at the counter to add items to bills instantly." },
  { icon: Tag, title: "MRP-Based Billing", desc: "Auto-calculate tax on MRP-inclusive prices for retail-friendly, faster checkout." },
  { icon: FileText, title: "Estimates & Quotations", desc: "Turn any quotation into an invoice in one click — no re-entry, no errors." },
  { icon: Printer, title: "Custom Invoice Designs", desc: "Add your logo, choose from clean templates, print on thermal or A4 with one click." },
  { icon: Layers, title: "Hardware Integrations", desc: "Works with thermal printers, weighing scales and barcode scanners out of the box." },
  { icon: TrendingUp, title: "Bill-Wise Profit Tracking", desc: "See your margin on every single bill and price your items smarter." },
  { icon: Package, title: "Delivery Challan & Orders", desc: "Track orders from creation to delivery, with proper documentation at every step." },
  { icon: ScanLine, title: "OCR Scan to Bill", desc: "Scan purchase bills with your camera — items and totals get parsed automatically." },
  { icon: Users, title: "Salesman Tracking", desc: "GPS-based tracking for field sales so you always know team activity in real time." },
  { icon: ArrowUpRight, title: "Tally Data Migration", desc: "Move from Tally without losing records — import and export both directions." },
  { icon: Sparkles, title: "AI Business Assistant", desc: "Ask questions about your data, get sales forecasts, categorise expenses instantly." },
];

const SOLUTION_BLOCKS = [
  { icon: ReceiptText, title: "Accurate GST Billing & Invoicing",
    desc: "Create error-free GST invoices and generate e-invoices instantly with built-in compliance and automated tax calculations.",
    cta: "Explore Invoicing" },
  { icon: Package, title: "Smart Inventory Management",
    desc: "Track stock levels in real time, get low-stock alerts and manage warehouses and batches — all from one screen.",
    cta: "Explore Inventory" },
  { icon: ShoppingCart, title: "Fast POS Billing",
    desc: "Speed up counter sales with reliable POS billing, barcode scanning, keyboard shortcuts and thermal printer support.",
    cta: "Explore POS" },
  { icon: BarChart3, title: "Professional Reports",
    desc: "Sales, P&L, GST, inventory and party ledger reports — export to PDF or Excel in one click.",
    cta: "Explore Reports" },
];

const IMPACT = [
  { icon: ReceiptText, k: "5,00,000+", label: "Bills Created Today" },
  { icon: FileCheck, k: "10,000+", label: "GST Returns Prepared" },
  { icon: Building2, k: "1M+", label: "Businesses Using TheBillPro" },
];

const INNOVATION = [
  { badge: "Sell Online", title: "Sell Your Products Online Easily",
    desc: "Create your own online store, share the link with customers and start selling immediately. No coding required.",
    icon: Store },
  { badge: "WhatsApp Marketing", title: "Connect With Customers Directly",
    desc: "Share offers, order updates and bills on WhatsApp — all in a few clicks.",
    icon: MessageCircle },
  { badge: "Smart Ads", title: "Promote Your Business Easily",
    desc: "Create simple ads for your products in seconds — no marketing skills required.",
    icon: TrendingUp },
];

const LANGUAGES = ["English", "हिंदी", "தமிழ்", "తెలుగు", "मराठी", "ગુજરાતી", "বাংলা", "ಕನ್ನಡ", "മലയാളം"];

const PRESS = ["Yahoo Finance", "India Today", "Business Standard", "Business Today", "The Tribune", "Good Returns"];

const INDUSTRIES_MAIN = [
  { icon: Store, title: "Retailers & Kirana Stores",
    desc: "Manage daily sales with GST and non-GST bills, track inventory, print thermal receipts and grow with reports." },
  { icon: ShoppingBag, title: "Wholesalers & Distributors",
    desc: "Handle bulk orders, credit periods, dispatch and party ledgers — all with one tap." },
  { icon: Hammer, title: "Manufacturers & Contractors",
    desc: "Streamline production, raw material consumption, work orders and site-wise billing." },
];

const INDUSTRIES_MINI = [
  { icon: ShoppingCart, title: "Grocery Store", desc: "Fast billing and stock built for kirana stores and small retailers." },
  { icon: Store, title: "Supermarket", desc: "High-volume billing, barcode scanning, aisle-wise stock." },
  { icon: Gem, title: "Jewellery Store", desc: "Gold and silver billing with accurate pricing and GST-ready bills." },
  { icon: Shirt, title: "Cloth Shop", desc: "Size, colour and variant billing with easy discounts." },
  { icon: Zap, title: "Electric & Electronics", desc: "Warranty tracking and GST billing for appliance shops." },
  { icon: Utensils, title: "Restaurant", desc: "Order billing with KOT, table management and split checks." },
  { icon: Cross, title: "Pharmacy", desc: "Medicine billing, expiry tracking and GSTR-ready reports." },
  { icon: Building, title: "Hospital & Clinic", desc: "Patient billing, service charges and receipts made simple." },
  { icon: BedDouble, title: "Hotels", desc: "Room billing, guest records and check-in/out receipts." },
  { icon: Scissors, title: "Salon & Spa", desc: "Services, appointments and package billing in one place." },
  { icon: Bus, title: "Transportation", desc: "Trip billing, expenses and multi-party payments." },
  { icon: Phone, title: "Mobile Shop", desc: "Serial-number billing, warranty and IMEI tracking." },
];

const FAQ_ITEMS = [
  { q: "What is billing software?",
    a: "Billing software like TheBillPro is a digital tool that helps businesses create GST-compliant invoices, track payments, manage inventory and automate daily billing operations from one platform. It replaces messy Excel sheets and manual paperwork." },
  { q: "What is GST billing software?",
    a: "GST billing software helps you create tax invoices, calculate CGST/SGST/IGST automatically, generate HSN-wise summaries and prepare data for GSTR-1 and GSTR-3B filing." },
  { q: "Who can use TheBillPro?",
    a: "Retailers, wholesalers, manufacturers, distributors, restaurants, pharmacies, freelancers, interior designers, contractors and other small businesses — anyone who bills customers can use TheBillPro." },
  { q: "Is TheBillPro easy to use?",
    a: "Yes. The interface is designed for owners who aren't accountants. You can create your first invoice in under 60 seconds, no training required." },
  { q: "Is TheBillPro free to use?",
    a: "You get a 14-day free trial with full access. After the trial, TheBillPro is available at ₹99/month, with higher plans for multi-user and multi-business setups." },
  { q: "Does TheBillPro work on Android and iOS?",
    a: "TheBillPro is a responsive web app — it works beautifully on Android, iPhone, iPad, Windows and Mac. All you need is a browser." },
  { q: "Can I use TheBillPro offline?",
    a: "TheBillPro is a cloud-first app, so a stable internet connection is recommended. Offline PWA support is on our roadmap." },
  { q: "Is my business data safe with TheBillPro?",
    a: "Yes. All data is encrypted in transit and stored on secure cloud infrastructure. Your business data is never auto-deleted, even if you cancel your subscription." },
  { q: "Can I share GST bills on WhatsApp?",
    a: "Yes. Every invoice, quotation and payment receipt can be shared via WhatsApp or email in one tap." },
  { q: "How do I get started with TheBillPro?",
    a: "Just sign up on this page — no credit card required. Add your business details, add customers and items, and create your first invoice within a minute." },
];

/* -------------------------------- component ------------------------------- */
export default function Landing() {
  return (
    <PublicLayout>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10 bg-gradient-to-b from-primary/5 via-background to-background" />
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 pt-14 pb-12 sm:pt-20 sm:pb-16">
          <div className="text-center">
            <Badge variant="secondary" className="gap-1.5" data-testid="hero-trust-badge">
              <Sparkles size={12} /> Trusted by 1M+ Indian Businesses
            </Badge>
          </div>
          <div className="mt-6 grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]" data-testid="hero-title">
                Best GST Billing Software <br/>for <span className="text-primary">Small Businesses</span>
              </h1>
              <p className="mt-5 text-base sm:text-lg text-muted-foreground leading-relaxed max-w-xl">
                Easy GST billing, tax compliance, payment tracking, inventory control and simple accounting —
                everything a growing Indian business needs, in one clean platform. Start your free trial today.
              </p>
              <div className="mt-7 flex flex-wrap gap-3 items-center">
                <Button asChild size="lg" data-testid="hero-cta-signup">
                  <Link to="/signup">Start Free Trial <ArrowRight size={16} className="ml-1" /></Link>
                </Button>
                <Button asChild variant="outline" size="lg" data-testid="hero-cta-pricing">
                  <Link to="/pricing">View Pricing</Link>
                </Button>
              </div>
              <div className="mt-6 flex flex-wrap gap-4 text-xs text-muted-foreground">
                <span className="flex items-center gap-1"><Check size={14} className="text-primary" /> No credit card</span>
                <span className="flex items-center gap-1"><Check size={14} className="text-primary" /> 14-day free trial</span>
                <span className="flex items-center gap-1"><Check size={14} className="text-primary" /> Cancel anytime</span>
              </div>
              {/* Rating badges */}
              <div className="mt-8 flex flex-wrap gap-3">
                {[
                  { name: "Google Play", rating: "4.8" },
                  { name: "SourceForge", rating: "4.9" },
                  { name: "G2 Reviews", rating: "4.4" },
                ].map((r) => (
                  <div key={r.name} className="flex items-center gap-2 px-3 py-2 rounded-lg border bg-card" data-testid={`rating-${r.name.replace(/\s+/g, "-").toLowerCase()}`}>
                    <div className="flex items-center gap-0.5 text-yellow-500">
                      {Array.from({ length: 5 }).map((_, i) => <Star key={i} size={12} fill="currentColor" />)}
                    </div>
                    <div className="text-xs">
                      <div className="font-semibold leading-tight">{r.rating}</div>
                      <div className="text-muted-foreground leading-tight">{r.name}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Dashboard mockup */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
              <div className="rounded-2xl border shadow-2xl overflow-hidden bg-card">
                <div className="bg-muted h-8 flex items-center gap-1.5 px-3">
                  <div className="w-2.5 h-2.5 rounded-full bg-destructive/60" />
                  <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/60" />
                  <div className="w-2.5 h-2.5 rounded-full bg-emerald-500/60" />
                  <div className="ml-2 text-[10px] text-muted-foreground">app.thebillpro.com/dashboard</div>
                </div>
                <div className="p-4 sm:p-6 grid grid-cols-2 gap-3">
                  {[
                    { k: "Today's Sales", v: "₹42,500", c: "text-emerald-600" },
                    { k: "Outstanding", v: "₹1,28,000", c: "text-orange-600" },
                    { k: "Monthly Sales", v: "₹5,40,000", c: "text-primary" },
                    { k: "Profit", v: "₹1,85,000", c: "text-emerald-600" },
                  ].map((s) => (
                    <div key={s.k} className="p-3 rounded-lg bg-muted/50 border">
                      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{s.k}</div>
                      <div className={`font-heading font-bold text-lg mt-1 ${s.c}`}>{s.v}</div>
                    </div>
                  ))}
                  <div className="col-span-2 p-3 rounded-lg bg-muted/50 border">
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Sales · Last 7 days</div>
                    <div className="flex items-end gap-1 h-16">
                      {[30, 55, 40, 70, 50, 85, 60].map((h, i) => (
                        <div key={i} className="flex-1 rounded-t bg-primary/70" style={{ height: `${h}%` }} />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="border-y bg-muted/30">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6 grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { icon: ShieldCheck, label: "GST-Ready Billing" },
            { icon: Lock, label: "Secure SSL Data Protection" },
            { icon: Award, label: "Trusted by Business Owners" },
            { icon: Building2, label: "Used by Top Retail & Manufacturing Brands" },
          ].map((t) => (
            <div key={t.label} className="flex items-center gap-3 text-sm">
              <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary grid place-items-center flex-shrink-0"><t.icon size={18} /></div>
              <span className="font-medium leading-tight">{t.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* NATIONWIDE COVERAGE */}
      <section className="max-w-[1400px] mx-auto px-4 sm:px-6 py-16 sm:py-24">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2 text-xs font-semibold text-destructive uppercase tracking-widest">
              <MapPin size={14} /> Nationwide Coverage
            </div>
            <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-bold tracking-tight">
              Supporting Local Small &amp; Medium Businesses Across Every Corner of India
            </h2>
            <p className="mt-4 text-muted-foreground">
              From neighbourhood kirana stores to growing manufacturers, TheBillPro is built for every Indian
              small business. Local language support, cloud reliability and workflows designed around how SME
              owners actually work.
            </p>
            <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-4">
              {TRUST_STATS.map((s) => (
                <div key={s.label} className="p-4 rounded-xl bg-card border" data-testid={`trust-${s.k.replace(/\W/g, "")}`}>
                  <div className="font-heading font-bold text-2xl">{s.k}</div>
                  <div className="text-xs text-muted-foreground mt-1 leading-tight">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2 text-xs text-muted-foreground">
            {["Delhi", "Mumbai", "Bengaluru", "Chennai", "Hyderabad", "Kolkata", "Ahmedabad", "Pune", "Jaipur", "Lucknow", "Surat", "Kanpur", "Nagpur", "Indore", "Patna", "Bhopal", "Ludhiana", "Coimbatore"].map((c) => (
              <div key={c} className="p-3 rounded-lg border bg-card flex items-center gap-2">
                <MapPin size={12} className="text-primary" /> {c}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BUSINESS DAY JOURNEY */}
      <section className="bg-muted/30 border-y">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-16 sm:py-24">
          <div className="text-center max-w-2xl mx-auto">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Your Business Journey, Simpler &amp; Safer</h2>
            <p className="mt-3 text-muted-foreground">TheBillPro walks with you from the moment you open your shop until you close for the day.</p>
          </div>
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {JOURNEY.map((j) => (
              <Card key={j.n} className="p-6 hover:border-primary/40 transition-colors" data-testid={`journey-${j.n}`}>
                <div className="text-xs font-mono text-muted-foreground">{j.n}</div>
                <div className="mt-1 text-[10px] uppercase tracking-widest text-primary font-semibold">{j.time}</div>
                <div className="mt-4 w-10 h-10 rounded-lg bg-primary/10 text-primary grid place-items-center">
                  <j.icon size={18} />
                </div>
                <div className="mt-3 font-heading font-bold text-lg">{j.title}</div>
                <div className="text-sm text-muted-foreground mt-1">{j.desc}</div>
                <div className="mt-4 pt-4 border-t text-xs font-semibold">{j.tag} →</div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* MULTI-DEVICE */}
      <section className="max-w-[1400px] mx-auto px-4 sm:px-6 py-16 sm:py-24">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">The Real Multi-Device Experience</h2>
            <p className="mt-4 text-muted-foreground">
              TheBillPro's cloud sync gives you the freedom to manage and monitor your business from mobile and desktop —
              whether you&apos;re at a supplier&apos;s warehouse or on holiday.
            </p>
            <div className="mt-8 grid grid-cols-2 gap-4 max-w-md">
              <div className="p-4 rounded-xl bg-primary/5 border border-primary/20">
                <div className="font-heading font-bold text-2xl text-primary">99.9%</div>
                <div className="text-xs text-muted-foreground mt-1">Uptime Reliable</div>
              </div>
              <div className="p-4 rounded-xl bg-primary/5 border border-primary/20">
                <div className="font-heading font-bold text-2xl text-primary">Instant</div>
                <div className="text-xs text-muted-foreground mt-1">Cloud Sync</div>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Card className="p-6 text-center">
              <Smartphone size={40} className="mx-auto text-primary" />
              <div className="mt-3 font-heading font-bold">Mobile</div>
              <div className="text-xs text-muted-foreground mt-1">Bill on the go</div>
            </Card>
            <Card className="p-6 text-center">
              <Cloud size={40} className="mx-auto text-primary" />
              <div className="mt-3 font-heading font-bold">Cloud Sync</div>
              <div className="text-xs text-muted-foreground mt-1">Everywhere, instantly</div>
            </Card>
            <Card className="p-6 text-center">
              <Wifi size={40} className="mx-auto text-primary" />
              <div className="mt-3 font-heading font-bold">Desktop</div>
              <div className="text-xs text-muted-foreground mt-1">Full power, big screen</div>
            </Card>
            <Card className="p-6 text-center">
              <Lock size={40} className="mx-auto text-primary" />
              <div className="mt-3 font-heading font-bold">Secure</div>
              <div className="text-xs text-muted-foreground mt-1">Encrypted at rest</div>
            </Card>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="bg-muted/30 border-y">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-16 sm:py-24">
          <div className="text-center max-w-2xl mx-auto">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Why Businesses Choose TheBillPro</h2>
            <p className="mt-3 text-muted-foreground">Real reviews from real users.</p>
          </div>
          <div className="mt-10 grid md:grid-cols-3 gap-4">
            <Card className="p-6 bg-primary text-primary-foreground border-primary">
              <div className="font-heading font-bold text-4xl">80%</div>
              <div className="text-xs uppercase tracking-widest opacity-90 mt-1">Time Saved</div>
              <div className="text-sm opacity-90 mt-2">Manual paperwork reduced</div>
            </Card>
            <Card className="p-6">
              <div className="flex items-center gap-2 text-yellow-500 mb-3">
                {Array.from({ length: 5 }).map((_, i) => <Star key={i} size={14} fill="currentColor" />)}
              </div>
              <p className="text-sm leading-relaxed">
                &ldquo;Switched to TheBillPro this year. Now able to manage GST billing, barcode billing and inventory
                all in one place. Recommend to every shop owner.&rdquo;
              </p>
              <div className="mt-4 pt-4 border-t flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/10 text-primary grid place-items-center font-semibold">A</div>
                <div>
                  <div className="font-semibold text-sm">Sample Customer</div>
                  <div className="text-xs text-muted-foreground">Gift Shop Owner</div>
                </div>
              </div>
            </Card>
            <Card className="p-6 bg-primary text-primary-foreground border-primary">
              <div className="font-heading font-bold text-4xl">100%</div>
              <div className="text-xs uppercase tracking-widest opacity-90 mt-1">Accuracy</div>
              <div className="text-sm opacity-90 mt-2">GST calculations, every time</div>
            </Card>
          </div>
        </div>
      </section>

      {/* KEY FEATURES GRID */}
      <section className="max-w-[1400px] mx-auto px-4 sm:px-6 py-16 sm:py-24">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 text-xs font-semibold text-primary uppercase tracking-widest">
            <Sparkles size={14} /> Built for India
          </div>
          <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-bold tracking-tight">Key Billing Features Built for Indian SME Owners</h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {FEATURES.map((f) => (
            <Card key={f.title} className="p-5 hover:border-primary/40 transition-colors" data-testid={`feature-${f.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
              <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary grid place-items-center mb-3">
                <f.icon size={18} />
              </div>
              <div className="font-heading font-semibold text-sm">{f.title}</div>
              <div className="text-xs text-muted-foreground mt-1 leading-relaxed">{f.desc}</div>
            </Card>
          ))}
        </div>
      </section>

      {/* COMPLETE SOLUTION */}
      <section className="bg-muted/30 border-y">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-16 sm:py-24">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Complete Billing Solution for Better Business Management</h2>
            <p className="mt-3 text-muted-foreground">Everything you expect from the best billing software, in one seamless platform.</p>
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            {SOLUTION_BLOCKS.map((b, i) => (
              <Card key={b.title} className={`p-8 ${i % 2 === 1 ? "md:mt-8" : ""}`}>
                <div className="w-12 h-12 rounded-xl bg-primary/10 text-primary grid place-items-center mb-4">
                  <b.icon size={22} />
                </div>
                <div className="font-heading font-bold text-xl">{b.title}</div>
                <p className="mt-2 text-muted-foreground text-sm leading-relaxed">{b.desc}</p>
                <Button asChild variant="link" className="p-0 mt-3 h-auto">
                  <Link to="/features">{b.cta} <ArrowRight size={12} className="ml-1" /></Link>
                </Button>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* HOW TO SETUP */}
      <section className="max-w-[1400px] mx-auto px-4 sm:px-6 py-16 sm:py-24">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">How to Set Up TheBillPro Quickly</h2>
          <p className="mt-3 text-muted-foreground">Start billing in under 60 seconds with 4 simple steps.</p>
        </div>
        <div className="grid md:grid-cols-4 gap-4">
          {[
            { n: 1, t: "Sign Up Free", d: "Create your TheBillPro account and start your 14-day free trial." },
            { n: 2, t: "Set Up Your Business", d: "Add business details, upload logo and configure GST." },
            { n: 3, t: "Add Items & Customers", d: "Add products or services with prices and GST rates." },
            { n: 4, t: "Create Bills", d: "Choose a template, add line items and share via WhatsApp or PDF." },
          ].map((s) => (
            <div key={s.n} className="relative">
              <Card className="p-6">
                <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground grid place-items-center font-heading font-bold">{s.n}</div>
                <div className="mt-4 font-heading font-bold">{s.t}</div>
                <div className="mt-1 text-sm text-muted-foreground">{s.d}</div>
              </Card>
            </div>
          ))}
        </div>
      </section>

      {/* IMPACT COUNTERS */}
      <section className="bg-primary text-primary-foreground">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-16 sm:py-20">
          <div className="text-center mb-10">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">How TheBillPro Is Making a Difference</h2>
            <p className="mt-3 opacity-90">See how digital billing is transforming small businesses across India.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            {IMPACT.map((s) => (
              <div key={s.label} className="text-center">
                <div className="w-14 h-14 rounded-2xl bg-white/10 backdrop-blur grid place-items-center mx-auto"><s.icon size={22} /></div>
                <div className="mt-4 font-heading font-bold text-4xl sm:text-5xl">{s.k}</div>
                <div className="mt-1 text-sm opacity-90">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INNOVATION */}
      <section className="max-w-[1400px] mx-auto px-4 sm:px-6 py-16 sm:py-24">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <div className="inline-flex items-center gap-2 text-xs font-semibold text-primary uppercase tracking-widest">
            <Sparkles size={14} /> High-Tech Innovation
          </div>
          <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-bold tracking-tight">Smart Tools for Growing Businesses</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          {INNOVATION.map((it) => (
            <Card key={it.title} className="p-6">
              <Badge variant="secondary" className="text-xs">{it.badge}</Badge>
              <div className="mt-3 w-12 h-12 rounded-xl bg-primary/10 text-primary grid place-items-center"><it.icon size={22} /></div>
              <div className="mt-4 font-heading font-bold text-lg">{it.title}</div>
              <div className="mt-1 text-sm text-muted-foreground">{it.desc}</div>
            </Card>
          ))}
        </div>
      </section>

      {/* MULTI-LANGUAGE */}
      <section className="bg-muted/30 border-y">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-16 sm:py-20 text-center">
          <div className="inline-flex items-center gap-2 text-xs font-semibold text-primary uppercase tracking-widest">
            <Globe size={14} /> 9+ Language Support
          </div>
          <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-bold tracking-tight">Multi-Language Support Across India</h2>
          <p className="mt-3 text-muted-foreground max-w-2xl mx-auto">
            Whether you&apos;re in a metro, a small town or a rural village, get help in the language you speak.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-2 max-w-3xl mx-auto">
            {LANGUAGES.map((lang) => (
              <span key={lang} className="px-4 py-2 rounded-full border bg-card text-sm font-medium">{lang}</span>
            ))}
          </div>
          <div className="mt-6 text-xs text-muted-foreground">Priority Support · 9+ Languages · Empowering MSMEs beyond language barriers</div>
        </div>
      </section>

      {/* PRESS */}
      <section className="max-w-[1400px] mx-auto px-4 sm:px-6 py-14">
        <div className="text-center mb-8">
          <div className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">TheBillPro Covered In</div>
        </div>
        <div className="flex flex-wrap justify-center items-center gap-x-10 gap-y-4 opacity-70">
          {PRESS.map((p) => (
            <div key={p} className="text-sm font-heading font-bold tracking-tight text-muted-foreground">
              {p}
            </div>
          ))}
        </div>
      </section>

      {/* INDUSTRIES MAIN */}
      <section className="bg-muted/30 border-y">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-16 sm:py-24">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <div className="inline-flex items-center gap-2 text-xs font-semibold text-primary uppercase tracking-widest">
              <Briefcase size={14} /> For Every Business Type
            </div>
            <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-bold tracking-tight">One Billing Software Across Industries</h2>
            <p className="mt-3 text-muted-foreground">Built for everyday Indian businesses that need simple billing and better control.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            {INDUSTRIES_MAIN.map((it) => (
              <Card key={it.title} className="p-6 hover:border-primary/40 transition-colors">
                <div className="w-12 h-12 rounded-xl bg-primary/10 text-primary grid place-items-center mb-4"><it.icon size={22} /></div>
                <div className="font-heading font-bold text-lg">{it.title}</div>
                <div className="text-sm text-muted-foreground mt-2">{it.desc}</div>
                <Button asChild variant="link" className="p-0 mt-3 h-auto"><Link to="/features">Learn more <ArrowRight size={12} className="ml-1" /></Link></Button>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* MOBILE APP CTA */}
      <section className="max-w-[1400px] mx-auto px-4 sm:px-6 py-16 sm:py-24 grid lg:grid-cols-2 gap-10 items-center">
        <div>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Manage Your Business with the TheBillPro App</h2>
          <p className="mt-3 text-muted-foreground">Carry your entire business operation in your pocket. Perfect for owners who are always on the move.</p>
          <div className="mt-6 space-y-3">
            {[
              { icon: Smartphone, t: "Works on Android & iOS", d: "Seamless experience across every device." },
              { icon: Sparkles, t: "Free 14-Day Trial", d: "Get started free with all core features unlocked." },
              { icon: Cloud, t: "Instant Cloud Sync", d: "Mobile changes reflect on desktop in real time." },
            ].map((b) => (
              <div key={b.t} className="flex gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary grid place-items-center flex-shrink-0"><b.icon size={16} /></div>
                <div>
                  <div className="font-semibold text-sm">{b.t}</div>
                  <div className="text-xs text-muted-foreground">{b.d}</div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-8">
            <Button asChild size="lg"><Link to="/signup">Start Free Trial</Link></Button>
          </div>
        </div>
        <div className="flex justify-center">
          <div className="w-64 h-[440px] rounded-[36px] border-8 border-foreground/90 bg-card shadow-2xl overflow-hidden">
            <div className="h-6 bg-foreground/90 flex items-center justify-center">
              <div className="w-16 h-1.5 bg-background/40 rounded-full" />
            </div>
            <div className="p-4 space-y-3">
              <div className="text-xs font-semibold">Dashboard</div>
              {[
                { k: "Sales", v: "₹12,500", c: "text-emerald-600" },
                { k: "Pending", v: "₹4,000", c: "text-orange-600" },
                { k: "Stock", v: "128 items", c: "text-primary" },
              ].map((s) => (
                <div key={s.k} className="p-3 rounded-lg border">
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{s.k}</div>
                  <div className={`font-bold text-lg ${s.c}`}>{s.v}</div>
                </div>
              ))}
              <div className="p-3 rounded-lg border">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">7-Day Sales</div>
                <div className="flex items-end gap-1 h-14">
                  {[35, 60, 45, 70, 55, 85, 65].map((h, i) => <div key={i} className="flex-1 rounded-t bg-primary/70" style={{ height: `${h}%` }} />)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHO CAN USE - INDUSTRY GRID */}
      <section className="bg-muted/30 border-y">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-16 sm:py-24">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Who Can Use TheBillPro?</h2>
            <p className="mt-3 text-muted-foreground">A complete billing platform for small businesses across industries.</p>
          </div>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {INDUSTRIES_MINI.map((it) => (
              <Card key={it.title} className="p-5 hover:border-primary/40 transition-colors">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary grid place-items-center flex-shrink-0"><it.icon size={18} /></div>
                  <div>
                    <div className="font-heading font-semibold text-sm">{it.title}</div>
                    <div className="text-xs text-muted-foreground mt-1 leading-relaxed">{it.desc}</div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
        <div className="text-center mb-10">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Frequently Asked Questions</h2>
          <p className="mt-3 text-muted-foreground">Everything you need to know about TheBillPro.</p>
        </div>
        <div className="space-y-3">
          {FAQ_ITEMS.map((it) => (
            <Card key={it.q} className="p-5">
              <details className="group">
                <summary className="font-heading font-semibold cursor-pointer flex items-center justify-between gap-4">
                  <span>{it.q}</span>
                  <span className="text-muted-foreground group-open:rotate-180 transition-transform flex-shrink-0">▾</span>
                </summary>
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{it.a}</p>
              </details>
            </Card>
          ))}
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="max-w-[1200px] mx-auto px-4 sm:px-6 pb-24">
        <Card className="p-8 sm:p-16 text-center bg-primary text-primary-foreground border-primary">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 backdrop-blur text-xs font-semibold uppercase tracking-widest mb-4">
            <Sparkles size={12} /> Ready when you are
          </div>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Start Your Free Trial Today</h2>
          <p className="mt-3 opacity-90 max-w-lg mx-auto">Join thousands of Indian small businesses simplifying their billing with TheBillPro. No credit card. Cancel anytime.</p>
          <div className="mt-8 flex justify-center gap-3 flex-wrap">
            <Button asChild size="lg" variant="secondary" data-testid="closing-cta-signup"><Link to="/signup">Start Free Trial</Link></Button>
            <Button asChild size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 hover:text-white">
              <Link to="/pricing">See Pricing</Link>
            </Button>
          </div>
          <div className="mt-6 text-xs opacity-80">₹99/month · 14-day free trial · Cancel anytime</div>
        </Card>
      </section>
    </PublicLayout>
  );
}
